import React, { Component } from 'react';
import classNames from 'classnames';
import debounce from 'lodash/debounce';

import { injectIntl, intlShape } from '../../../util/reactIntl';

import { FieldTextInput } from '../../../components';

import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';

import css from './KeywordFilter.module.css';

// When user types, we wait for new keystrokes a while before searching new content
const DEBOUNCE_WAIT_TIME = 600;
// Short search queries (e.g. 2 letters) have a longer timeout before search is made
const TIMEOUT_FOR_SHORT_QUERIES = 2000;

const getKeywordQueryParam = queryParamNames => {
  return Array.isArray(queryParamNames)
    ? queryParamNames[0]
    : typeof queryParamNames === 'string'
    ? queryParamNames
    : 'keywords';
};

/**
 * KeywordFilter component
 * TODO: change to functional component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} props.id - The ID
 * @param {string} props.name - The name
 * @param {React.Node} props.label - The label
 * @param {Function} props.getAriaLabel - The function to retrieve the aria label for the component
 * @param {Array<string>} props.queryParamNames - The query param names
 * @param {Object} props.initialValues - The initial values
 * @param {Object} props.initialValues.keyword - The keyword
 * @param {Function} props.onSubmit - The function to submit
 * @param {number} [props.contentPlacementOffset] - The content placement offset
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */
class KeywordFilter extends Component {
  constructor(props) {
    super(props);

    this.shortKeywordTimeout = null;
    this.mobileInputRef = React.createRef();
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleChangeWithDebounce = this.handleChangeWithDebounce.bind(this);
    this.handleClear = this.handleClear.bind(this);
    this.flushPending = this.flushPending.bind(this);
    this.debouncedSubmit = debounce(this.handleSubmit, DEBOUNCE_WAIT_TIME);
  }

  componentDidUpdate(previousProps) {
    const param = getKeywordQueryParam(this.props.queryParamNames);
    if (previousProps.initialValues?.[param] !== this.props.initialValues?.[param]) {
      this.cancelPending();
    }
  }

  cancelPending() {
    window.clearTimeout(this.shortKeywordTimeout);
    this.debouncedSubmit.cancel();
    this.pendingValues = null;
  }

  componentWillUnmount() {
    this.cancelPending();
  }

  handleSubmit(values) {
    this.pendingValues = null;
    const { name, queryParamNames, onSubmit } = this.props;
    onSubmit({ [getKeywordQueryParam(queryParamNames)]: values ? values[name] : null });
  }

  handleChangeWithDebounce(values) {
    this.cancelPending();
    if (this.props.draftMode) return this.handleSubmit(values);
    if (!values) return this.handleSubmit(null);
    this.pendingValues = values;
    const keyword = values[this.props.name] || '';
    if (!keyword || keyword.length >= 3) {
      this.debouncedSubmit(values);
    } else {
      this.shortKeywordTimeout = window.setTimeout(
        () => this.handleSubmit(values),
        TIMEOUT_FOR_SHORT_QUERIES
      );
    }
  }

  handleClear() {
    this.cancelPending();
    if (this.mobileInputRef.current) this.mobileInputRef.current.value = '';
  }

  flushPending() {
    const values = this.pendingValues;
    this.cancelPending();
    if (values) this.handleSubmit(values);
  }

  render() {
    const {
      rootClassName,
      className,
      id,
      name,
      label,
      getAriaLabel,
      initialValues,
      contentPlacementOffset = 0,
      onSubmit,
      queryParamNames,
      intl,
      showAsPopup = true,
      ...rest
    } = this.props;

    const classes = classNames(rootClassName || css.root, className);

    const urlParam = getKeywordQueryParam(queryParamNames);
    const rawInitialValues = initialValues?.[urlParam];
    const hasInitialValues = !!initialValues && !!rawInitialValues && rawInitialValues.length > 0;
    const labelForPopup = hasInitialValues
      ? intl.formatMessage({ id: 'KeywordFilter.labelSelected' }, { labelText: rawInitialValues })
      : label;

    const labelClass = hasInitialValues ? css.labelPlainSelected : css.labelPlain;
    const labelForPlain = <span className={labelClass}>{label}</span>;

    const filterText = intl.formatMessage({ id: 'KeywordFilter.filterText' });
    const placeholder = intl.formatMessage({ id: 'KeywordFilter.placeholder' });

    // pass the initial values with the name key so that
    // they can be passed to the correct field
    const namedInitialValues = { [name]: rawInitialValues };

    return showAsPopup ? (
      <FilterPopup
        className={classes}
        rootClassName={rootClassName}
        popupClassName={css.popupSize}
        name={name}
        label={labelForPopup}
        ariaLabel={getAriaLabel(label, rawInitialValues)}
        isSelected={hasInitialValues}
        id={`${id}.popup`}
        showAsPopup
        labelMaxWidth={250}
        contentPlacementOffset={contentPlacementOffset}
        onSubmit={this.handleSubmit}
        initialValues={namedInitialValues}
        keepDirtyOnReinitialize
        {...rest}
      >
        <FieldTextInput
          className={css.field}
          name={name}
          id={`${id}-input`}
          type="text"
          label={filterText}
          placeholder={placeholder}
          autoComplete="off"
        />
      </FilterPopup>
    ) : (
      <FilterPlain
        className={className}
        rootClassName={rootClassName}
        label={labelForPlain}
        ariaLabel={getAriaLabel(label, rawInitialValues)}
        isSelected={hasInitialValues}
        id={`${id}.plain`}
        liveEdit
        onSubmit={this.handleChangeWithDebounce}
        onClear={this.handleClear}
        initialValues={namedInitialValues}
        {...rest}
      >
        <div className={css.fieldPlain} onBlur={this.flushPending}>
          <label className={css.fieldPlainLabel} htmlFor={`${id}-input`}>
            {filterText}
          </label>
          <FieldTextInput
            name={name}
            id={`${id}-input`}
            className={css.fieldPlainInput}
            inputRef={this.mobileInputRef}
            type="text"
            placeholder={placeholder}
            autoComplete="off"
          />
        </div>
      </FilterPlain>
    );
  }
}

export default injectIntl(KeywordFilter);
